/**
 * ThemeContext - Global theme management with persistence
 *
 * Features:
 * - Instant theme loading from localStorage (prevents flash)
 * - Syncs with backend for authoritative state
 * - Applies theme to document root via data-theme attribute
 * - Supports light, dark, and system preferences
 */
import React, { createContext, useState, useEffect, useContext } from 'react';
import api from '../services/api';

const ThemeContext = createContext();

export const ThemeProvider = ({ children }) => {
  const [theme, setTheme] = useState('light');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // 1. Check localStorage first for instant loading (prevents flash)
    const cachedTheme = localStorage.getItem('theme');
    if (cachedTheme && ['light', 'dark', 'system'].includes(cachedTheme)) {
      applyTheme(cachedTheme);
      setTheme(cachedTheme);
    }

    // 2. Fetch from backend (authoritative source)
    const fetchUserTheme = async () => {
      try {
        const response = await api.get('/profile/me');
        const userTheme = response.data.theme_preference || 'light';

        // Only update if different from cached
        if (userTheme !== cachedTheme) {
          setTheme(userTheme);
          applyTheme(userTheme);
          localStorage.setItem('theme', userTheme);
        }
      } catch (err) {
        // If not authenticated or error, keep cached theme or default
        console.log('Could not load theme from backend, using cached or default');
      } finally {
        setLoading(false);
      }
    };

    fetchUserTheme();
  }, []);

  /**
   * Apply theme to DOM by setting data-theme attribute on root element
   */
  const applyTheme = (themeName) => {
    const effectiveTheme = resolveTheme(themeName);
    document.documentElement.setAttribute('data-theme', effectiveTheme);
  };

  /**
   * Resolve 'system' preference to actual light/dark
   */
  const resolveTheme = (themeName) => {
    if (themeName === 'system') {
      // Check system preference
      if (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) {
        return 'dark';
      }
      return 'light';
    }
    return themeName;
  };

  /**
   * Change theme and persist to backend
   */
  const changeTheme = async (newTheme) => {
    if (!['light', 'dark', 'system'].includes(newTheme)) {
      console.error(`Invalid theme: ${newTheme}`);
      return;
    }

    // Update state and DOM immediately for instant feedback
    setTheme(newTheme);
    applyTheme(newTheme);
    localStorage.setItem('theme', newTheme);

    // Persist to backend in background
    try {
      await api.put('/profile/me', { theme_preference: newTheme });
    } catch (err) {
      console.error('Failed to save theme to backend:', err);
      // Don't revert - local theme change still works
    }
  };

  /**
   * Listen for system theme changes when theme is set to 'system'
   */
  useEffect(() => {
    if (theme !== 'system') return;

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = () => {
      applyTheme('system');
    };

    // Modern browsers
    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', handleChange);
      return () => mediaQuery.removeEventListener('change', handleChange);
    }
    // Fallback for older browsers
    else if (mediaQuery.addListener) {
      mediaQuery.addListener(handleChange);
      return () => mediaQuery.removeListener(handleChange);
    }
  }, [theme]);

  const value = {
    theme,
    changeTheme,
    loading,
    effectiveTheme: resolveTheme(theme)
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};

/**
 * Hook to access theme context
 */
export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export default ThemeContext;
